/**
 * lib/acting/context — WHO is at the keyboard, ON WHOSE BEHALF, and ON WHAT
 * CREDENTIAL (ARC-01, tranche T0).
 *
 * ⛔ WHY THIS EXISTS. Seven rows of the 213-row walkthrough register are one
 * defect wearing seven coats: nothing in this frontend says who is acting. The
 * answer is re-derived ad hoc at roughly 32 store reads plus ~30 un-indexed
 * `useOperatorSession: true` flags, and the four things that must agree about it
 * — the copy on screen, the route guard, the step-up ceremony, and which token
 * `api()` puts on the wire — derive from none of them and disagree in ways that
 * have already cost this project real money:
 *
 *   · `lib/api/beneficiaries.ts:228` builds its own Authorization header, knows
 *     about impersonation and NOT about act-as, so a firm downloading a client's
 *     beneficiary CSV sends the FIRM's bearer at a client-tenant path.
 *   · `lib/use-step-up-challenge.ts:137` stamps step-up freshness on whatever
 *     token is ambient. Under act-as that is a minted token carrying no `aal`,
 *     so the TOTP ceremony 403s and cannot complete at all.
 *   · `lib/query-client.ts` keys the cache on the SUPABASE session, which act-as
 *     and impersonation never touch — so the operator, client A and client B all
 *     address the same cache entries.
 *
 * The register's own line: until this object exists, every string fix is
 * temporary.
 *
 * ── WHAT THIS MODULE IS ─────────────────────────────────────────────────────
 *
 * DERIVED, NEVER STORED. `firm-act-store.ts` and `impersonation-store.ts` stay
 * as the storage layer and keep the raw JWTs; this is the single READ surface
 * over them plus the Supabase session. There is no new state here, so there is
 * no new thing to keep in sync, which is the whole point.
 *
 * ── WHAT IT DELIBERATELY DOES NOT OWN ───────────────────────────────────────
 *
 * CAPABILITY. No `can(...)`, no role sets, no permission booleans. The backend
 * already holds the canonical model with a CI guard (`backend/src/lib/
 * tenant-roles.ts` + `lib/capabilities.ts` + `scripts/lint-role-sets.mjs`). A
 * second model on the client is a second source of truth and will drift; the
 * frontend has already grown eight hand-rolled role sets with no guard at all,
 * and adding a ninth here would be the wrong answer to ARC-01. `standing` and
 * `credential.canSatisfyStepUp` are facts about the SESSION, not policy, which
 * is exactly why they belong here and a permission list does not.
 *
 * ROUTE POLICY. `CLIENT_ONLY_PATHS` stays in `lib/authz/route-policy.ts`. This
 * module answers WHO; the policy answers WHICH PATHS; `lib/authz/act-as-nav.ts`
 * is the join and stays the join.
 *
 * REDIRECTS. `routes/app/_layout.tsx` keeps deciding where to send people. The
 * ordering of its gates is load-bearing and documents four separate regressions.
 *
 * BRANDING. `lib/use-active-branding.ts` keeps the "which brand wins" rule.
 *
 * THE RAW JWTs. They stay in the two stores. This module exposes
 * `credential.kind`; `bearerTokenForContext()` hands `api()` the string.
 *
 * ── SCOPE (2 Sep) ───────────────────────────────────────────────────────────
 *
 * Every screen, guard and hook outside `lib/` reads posture, subject, standing
 * and credential from here, and writes through the two control hooks below.
 * `scripts/lint-acting-context.mjs` fails the commit on any file outside `lib/`
 * that names a store module, a store hook, or a store liveness function.
 * Migration history and the tests that proved each step: `tasks/arc-01/SURVEY.md`.
 */

import { useMemo } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useFirmActStore, type ActiveFirmAct } from '../firm-act-store';
import { useImpersonationStore, type ActiveImpersonation } from '../impersonation-store';
import { useSession } from '../use-session';
import { supabase } from '../supabase';
import { normalizeRole, type Role } from '../authz/route-policy';

// The credential half lives in a LEAF module so `lib/api/client.ts` can import
// it without joining this file's React import cycle (see bearer.ts's header).
// Re-exported here so `lib/acting/context` stays the one place to import from
// for everything except `api()` itself, and so there is exactly ONE definition
// of a credential in the frontend.
import { credentialOf, livenessOf, type ActingCredential, type ActingSubject } from './bearer';
export { lastKnownActAsSubject, forgetLastKnownActAsSubject } from './last-act-as';
// ARC-01: the store RECORD types and the two non-React liveness reads, re-exported
// so a screen or hook that needs them imports from here and never from a store.
export type { ActiveFirmAct } from '../firm-act-store';
export type { ActiveImpersonation } from '../impersonation-store';
import { firmActStatus } from '../firm-act-store';
import { decodeImpersonatedBy, impersonationStatus } from '../impersonation-store';

/**
 * Non-React, SYNCHRONOUS: which minted credential (if any) is standing in right
 * now, and whether it is live. For call-time checks a React render cannot make
 * — the act-as renewal deciding whether to renew, the `/app` guard reading the
 * moment it runs, a dialog opened after the token lapsed.
 *
 * The independent verifier (2 Sep) found two screens deciding posture from the
 * stores' raw `firmActStatus()` / `impersonationStatus()` laundered through a
 * re-export on this file, which the lint could not see. Those functions are no
 * longer exported from here; this is the one sync read, and the lint names the
 * raw ones as offenders outside `lib/`.
 *
 * `{ kind: 'session', status: 'active' }` means "nobody is standing in" — the
 * operator's own session liveness is a network fact and is not claimed here.
 */
export function currentCredentialStatus(): {
  kind: ActingCredential['kind'];
  status: ActingCredential['status'];
} {
  const impersonation = impersonationStatus();
  if (impersonation !== 'none') return { kind: 'impersonation', status: impersonation };
  const actAs = firmActStatus();
  if (actAs !== 'none') return { kind: 'act-as', status: actAs };
  return { kind: 'session', status: 'active' };
}

export {
  bearerTokenForContext,
  type ActingSubject,
  credentialOf,
  livenessOf,
  type ActingCredential,
  type ContextBearer,
  type CredentialKind,
  type CredentialStatus,
} from './bearer';

// ── The four postures ───────────────────────────────────────────────────────

/**
 * The postures that exist in this product.
 *
 * The external signer, the external auditor and the partner-finder are real
 * surfaces but not postures this object models: the signer is never
 * authenticated (the magic-link token in the URL IS the credential, and
 * `lib/api/external-signing.ts` sends no bearer at all), and the other two are
 * ordinary signed-in sessions on their own layouts that never touch either
 * store. They read as `anonymous` and `self` respectively, and their routes own
 * them.
 */
export type Posture =
  | 'anonymous'
  | 'self'
  | 'acting-as-client'
  | 'impersonating';

/** The tenant every DATA call lands in, and who to name in copy. */


/** The logged-in human. NEVER the subject. */
export interface ActingOperator {
  readonly supabaseUserId: string | null;
  /**
   * The label the SESSION itself carries, which in this product is the email
   * address. The operator's authoritative human name is a network read
   * (`useOperatorMe()` in lib/api/operator-identity.ts) and is deliberately not
   * invented here: a second source for a person's name is the bug class §5 of
   * the survey exists to refuse.
   */
  readonly displayName: string | null;
  /**
   * `app_metadata.role` off the SUPABASE session, normalised. This is the
   * operator's OWN role, NOT the role a minted token carries inside the subject
   * tenant. Those differ by construction: a `manage` grant mints role 'admin'
   * inside the client tenant (backend/src/handlers/firm-act-as.ts,
   * `roleForAccessLevel`), and a b2c client administering their own synthetic
   * tenant also carries 'admin'. Role therefore cannot separate a firm standing
   * in from a client standing on their own feet, which is why posture exists.
   */
  readonly role: Role;
  readonly tenantId: string | null;
}

/**
 * The GRANT, not a permission.
 *
 * `read` and `manage` are the two levels a client sets on a firm's grant
 * (firm-act-store.ts). They are a contract between two parties, which is why
 * they live on this object while capability does not. `own` means nobody is
 * standing in.
 */
export type Standing = 'own' | 'read' | 'manage';

export interface ActingContext {
  readonly posture: Posture;
  readonly subject: ActingSubject;
  readonly operator: ActingOperator;
  readonly credential: ActingCredential;
  readonly standing: Standing;
  /** True when operator is not the subject. The one predicate copy should branch on. */
  readonly isStandingIn: boolean;
  /** Cache identity. MUST change on every posture or subject change. */
  readonly principalKey: string;
}

// ── Derivation ──────────────────────────────────────────────────────────────

/**
 * The operator facts this derivation needs from a Supabase session.
 *
 * Taken as a snapshot rather than a `Session` so the pure derivation can be
 * driven from hand-built states in a test without constructing a whole
 * supabase-js session object.
 */
export interface OperatorSessionSnapshot {
  readonly supabaseUserId: string | null;
  readonly displayName: string | null;
  /** Raw `app_metadata.role`; normalised by the derivation, never before. */
  readonly rawRole: string;
  readonly tenantId: string | null;
}

export interface ActingContextInput {
  readonly operator: OperatorSessionSnapshot | null;
  readonly firmAct: ActiveFirmAct | null;
  readonly impersonation: ActiveImpersonation | null;
  /** Injectable clock. Defaults to now; tests pin it to cross an expiry. */
  readonly now?: number;
}

/**
 * Liveness of a minted token, mirroring `firmActStatus()` /
 * `impersonationStatus()` EXACTLY, including their treatment of an unparseable
 * timestamp as expired rather than as live.
 *
 * Deliberately mirrors the non-clearing pair. `activeFirmActToken()` and
 * `activeImpersonationToken()` have the same shape but CLEAR the store as a side
 * effect of being read; a derived context that cleared state while answering a
 * question would make every render a mutation. Clearing stays where it is today,
 * inside `api()` and the explicit exits.
 */
/**
 * Derive the acting context from hand-held inputs. Pure: no stores, no session,
 * no clock of its own.
 *
 * ⚠ POSTURE AND LIVENESS ARE TWO AXES, AND CONFLATING THEM IS THE EASY MISTAKE.
 *
 * `posture` answers WHO is being stood in for. It is 'acting-as-client' whenever
 * the store holds a client, whether or not that client's token is still alive,
 * because the operator has not stopped standing in just because 15 minutes
 * passed: the context bar still names the client, and a data call must still
 * refuse to travel on the firm's own session.
 *
 * `credential.status` answers WHETHER YOU CAN STILL ACT.
 *
 * That split is what lets both existing idioms survive substitution unchanged,
 * and the two are NOT the same predicate:
 *
 *   useFirmActStore((s) => s.active !== null)   ==  posture === 'acting-as-client'
 *   firmActStatus() === 'active'                ==  posture === 'acting-as-client'
 *                                                   && credential.status === 'active'
 *
 * The first idiom is what most call sites use (`act-as-nav.ts:55` among them);
 * the second is what the route guard and `api()` use. They differ for the window
 * between a token lapsing and the next data call clearing the store. A naive
 * derivation picks one and silently changes the meaning of every site using the
 * other.
 *
 * Precedence mirrors `api()`'s token precedence exactly (impersonation, then
 * act-as, then the session), because posture and the token on the wire must
 * never disagree about who this request belongs to.
 */
export function deriveActingContext(input: ActingContextInput): ActingContext {
  const now = input.now ?? Date.now();

  const operator: ActingOperator = {
    supabaseUserId: input.operator?.supabaseUserId ?? null,
    displayName: input.operator?.displayName ?? null,
    // normalizeRole fails closed to 'user', the non-privileged portal floor, so
    // an absent or unknown role can never read as privileged here.
    role: normalizeRole(input.operator?.rawRole ?? ''),
    tenantId: input.operator?.tenantId ?? null,
  };
  const operatorKey = operator.supabaseUserId ?? 'anon';

  if (input.impersonation) {
    const imp = input.impersonation;
    return {
      posture: 'impersonating',
      subject: {
        // ⚠ NULL, AND THAT IS A REAL GAP THIS OBJECT MAKES VISIBLE.
        // ImpersonateStartResponse carries `targetTenantId`
        // (lib/api/superadmin.ts) and `useStartImpersonation` drops it on the
        // floor, so the store never holds it. The UI therefore cannot name the
        // tenant it is standing in and cannot key a cache on it. Closing that
        // means carrying the field through the store, which is a store change
        // and belongs to its own tranche. `principalKey` below keys on the
        // target USER so the cache is correct in the meantime.
        tenantId: null,
        displayName: imp.targetDisplay,
        kind: 'impersonated-user',
      },
      operator,
      credential: credentialOf('impersonation', livenessOf(imp.expiresAt, now), imp.expiresAt),
      // Impersonation has no accessLevel equivalent: an operator sits in the
      // user's own seat and can do what that user can do.
      standing: 'manage',
      isStandingIn: true,
      principalKey: `impersonating:${operatorKey}:${imp.targetUserId}`,
    };
  }

  if (input.firmAct) {
    const act = input.firmAct;
    return {
      posture: 'acting-as-client',
      subject: {
        tenantId: act.clientTenantId,
        displayName: act.clientDisplay,
        kind: 'client',
      },
      operator,
      credential: credentialOf('act-as', livenessOf(act.expiresAt, now), act.expiresAt),
      standing: act.accessLevel,
      isStandingIn: true,
      // Keyed on the CLIENT tenant, which is what makes two different clients
      // two different cache principals. The cache key in query-client.ts today
      // is built from the Supabase session, which act-as never touches, so the
      // operator and every client they open share one principal.
      principalKey: `act-as:${operatorKey}:${act.clientTenantId}`,
    };
  }

  if (input.operator) {
    return {
      posture: 'self',
      subject: {
        tenantId: operator.tenantId,
        displayName: null,
        kind: 'me',
      },
      operator,
      credential: credentialOf('session', 'active', null),
      standing: 'own',
      isStandingIn: false,
      principalKey: `self:${operatorKey}:${operator.tenantId ?? 'none'}`,
    };
  }

  return {
    posture: 'anonymous',
    subject: { tenantId: null, displayName: null, kind: 'me' },
    operator,
    credential: credentialOf('session', 'none', null),
    standing: 'own',
    isStandingIn: false,
    principalKey: 'anonymous',
  };
}

/** Read the operator facts off a Supabase session. */
export function operatorSnapshotFromSession(session: Session | null): OperatorSessionSnapshot | null {
  if (!session?.user) return null;
  const appMetadata = session.user.app_metadata as { role?: unknown; tenant_id?: unknown } | undefined;
  return {
    supabaseUserId: session.user.id,
    displayName: session.user.email ?? null,
    rawRole: typeof appMetadata?.role === 'string' ? appMetadata.role : '',
    tenantId: typeof appMetadata?.tenant_id === 'string' ? appMetadata.tenant_id : null,
  };
}

// ── Reads ───────────────────────────────────────────────────────────────────

/**
 * React read.
 *
 * The clock is read at render, exactly as `firmActStatus()` is today, so a token
 * that lapses between renders is reported live until something re-renders. That
 * is unchanged behaviour, not a new hazard: the pre-`fetch` guard in `api()` is
 * what actually refuses a dead credential, and it reads the clock at call time.
 */
export function useActingContext(): ActingContext {
  const { session } = useSession();
  const firmAct = useFirmActStore((s) => s.active);
  const impersonation = useImpersonationStore((s) => s.active);
  return useMemo(
    () => deriveActingContext({
      operator: operatorSnapshotFromSession(session),
      firmAct,
      impersonation,
    }),
    [session, firmAct, impersonation],
  );
}

/** The name to put in copy, or null when the answer is "you". */
export function useSubjectName(): string | null {
  return useActingContext().subject.displayName;
}

/** The one predicate copy should branch on. */
export function useIsStandingIn(): boolean {
  return useActingContext().isStandingIn;
}

// ── ARC-01: the ONLY door screens have to the two stores ─────────────────────
//
// Register row ARC-01 ("posture leakage", P0, RECURRING): seven sightings, one
// defect — screens deciding who they were talking to from whichever store they
// happened to import. The context above answers that; these hooks are the
// remaining reads and writes a screen legitimately needs, so that
// `useFirmActStore` / `useImpersonationStore` never appear outside `lib/`.
// `scripts/lint-acting-context.mjs` fails the commit if they do.

/** True while a firm operator is inside a client tenant on an act-as credential
 *  (live or lapsed — a lapsed one is still "acting", and the api layer is what
 *  refuses it loudly). */
export function useIsActingAsClient(): boolean {
  return useActingContext().posture === 'acting-as-client';
}

/** The client a firm is standing in for, for copy that names the subject
 *  instead of saying "your trust" to the operator; null when nobody is. */
export function useActAsClientName(): string | null {
  const ctx = useActingContext();
  return ctx.posture === 'acting-as-client' ? ctx.subject.displayName : null;
}

/**
 * The two WRITES a screen may make to the act-as store: enter a client (the
 * Client Book, the renewal hook) and leave it (the act-as bar). Selector hooks,
 * not `getState()`, so a test that mocks the store module with a bare selector
 * function still sees these calls.
 */
export function useFirmActControls(): {
  beginFirmAct: (a: ActiveFirmAct) => void;
  endFirmAct: () => void;
} {
  const beginFirmAct = useFirmActStore((s) => s.setFirmAct);
  const endFirmAct = useFirmActStore((s) => s.clear);
  return useMemo(() => ({ beginFirmAct, endFirmAct }), [beginFirmAct, endFirmAct]);
}

/** The impersonation twin of {@link useFirmActControls}. */
export function useImpersonationControls(): {
  beginImpersonation: (a: ActiveImpersonation) => void;
  endImpersonation: () => void;
} {
  const beginImpersonation = useImpersonationStore((s) => s.setImpersonation);
  const endImpersonation = useImpersonationStore((s) => s.clear);
  return useMemo(
    () => ({ beginImpersonation, endImpersonation }),
    [beginImpersonation, endImpersonation],
  );
}

/** What the impersonation banner shows. */
export interface ImpersonationView {
  /** The user being impersonated, for copy. */
  readonly subjectName: string;
  /** The super_admin's own user id, as the minted token itself records it. */
  readonly impersonatedBy: string;
  readonly expiresAt: string;
}

/**
 * The active impersonation, or null — null ALSO when the stored token does not
 * carry an `impersonatedBy` claim (a stale or foreign value in sessionStorage),
 * so the banner never announces an impersonation the credential cannot prove.
 * That guard used to be the banner's own `decodeImpersonatedBy(active.jwt)`;
 * it lives here now so the banner needs neither the store nor the token.
 */
export function useImpersonation(): ImpersonationView | null {
  const active = useImpersonationStore((s) => s.active);
  return useMemo(() => {
    if (!active) return null;
    const impersonatedBy = decodeImpersonatedBy(active.jwt);
    if (!impersonatedBy) return null;
    return { subjectName: active.targetDisplay, impersonatedBy, expiresAt: active.expiresAt };
  }, [active]);
}

/**
 * Non-React read.
 *
 * ⚠ ASYNC, WHERE THE SURVEY PROPOSED SYNCHRONOUS. supabase-js exposes no
 * synchronous session accessor, and reading its storage key directly is exactly
 * the trap that once made 73 route screenshots score green against a login page.
 * Every consumer the survey named for this read is already async (`api()`, a
 * router `beforeLoad`), and the posture-only consumers listed in §7 are all
 * React component bodies served by `useActingContext` above, so nothing needs a
 * synchronous full read.
 *
 * `readSession` is injectable so tests need not stand up supabase-js.
 */
export async function actingContext(
  readSession: () => Promise<Session | null> = defaultReadSession,
): Promise<ActingContext> {
  const firmAct = useFirmActStore.getState().active;
  const impersonation = useImpersonationStore.getState().active;
  const session = await readSession();
  return deriveActingContext({
    operator: operatorSnapshotFromSession(session),
    firmAct,
    impersonation,
  });
}

async function defaultReadSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session ?? null;
}

// ── The one fact a derived context cannot carry ─────────────────────────────

